import { CategorisationTreeQuestion, SubmissionAnswer } from '../types'
import { QuestionType } from '../enums'

export type AnswerMap = Record<
  string,
  { answerId?: string; answerInput?: string }
>

const selectedOption = (
  question: CategorisationTreeQuestion,
  answers: AnswerMap,
) => question.options?.find(o => o.id === answers[question.id]?.answerId)

const isAnswered = (
  question: CategorisationTreeQuestion,
  answers: AnswerMap,
): boolean => {
  const answer = answers[question.id]
  return answer?.answerId != null || answer?.answerInput != null
}

/**
 * Flattens the tree into the list of currently-visible questions, in tree
 * order. For each question we surface, recursively, both:
 * - the selected option's `nextQuestions` (option-level children, revealed
 *   only when THAT option is chosen); and
 * - the question's `questionChildren` (question-level children, revealed once
 *   the question has any answer).
 */
export const visibleQuestions = (
  questions: CategorisationTreeQuestion[],
  answers: AnswerMap,
): CategorisationTreeQuestion[] =>
  questions.flatMap(question => {
    const optionChildren =
      selectedOption(question, answers)?.nextQuestions ?? []
    const questionChildren = isAnswered(question, answers)
      ? (question.questionChildren ?? [])
      : []
    return [
      question,
      ...visibleQuestions(optionChildren, answers),
      ...visibleQuestions(questionChildren, answers),
    ]
  })

/**
 * Completeness only — every visible question has an answer. Correctness is
 * validated server-side on submit (Phase 1 requirement: no early validation).
 */
export const isComplete = (
  questions: CategorisationTreeQuestion[],
  answers: AnswerMap,
): boolean =>
  visibleQuestions(questions, answers).every(question => {
    const answer = answers[question.id]
    if (
      question.type === QuestionType.InputNumeric ||
      question.type === QuestionType.InputText
    ) {
      return !!answer?.answerInput
    }
    return !!answer?.answerId
  })

export const collectAnswers = (
  questions: CategorisationTreeQuestion[],
  answers: AnswerMap,
): SubmissionAnswer[] =>
  visibleQuestions(questions, answers).flatMap(
    (question): SubmissionAnswer[] => {
      const answer = answers[question.id]
      if (
        question.type === QuestionType.InputNumeric ||
        question.type === QuestionType.InputText
      ) {
        return answer?.answerInput
          ? [{ questionId: question.id, answerInput: answer.answerInput }]
          : []
      }
      return answer?.answerId
        ? [{ questionId: question.id, answerId: answer.answerId }]
        : []
    },
  )

export const NONE_INTENT_VALUE = 'NONE_OF_THESE_APPLY'

export const hasNoneIntent = (
  questions: CategorisationTreeQuestion[],
  answers: AnswerMap,
): boolean =>
  visibleQuestions(questions, answers).some(
    question => selectedOption(question, answers)?.value === NONE_INTENT_VALUE,
  )

/** Drops answers that belong to questions no longer on the visible path. */
export const pruneAnswers = (
  questions: CategorisationTreeQuestion[],
  answers: AnswerMap,
): AnswerMap => {
  const visibleIds = new Set(
    visibleQuestions(questions, answers).map(q => q.id),
  )
  return Object.fromEntries(
    Object.entries(answers).filter(([questionId]) =>
      visibleIds.has(questionId),
    ),
  )
}

export const latestByAttempt = <T extends { attempt: number }>(
  items: T[],
): T | undefined =>
  items.reduce<T | undefined>(
    (best, current) =>
      !best || current.attempt > best.attempt ? current : best,
    undefined,
  )
